
import { useState, useEffect } from "react";
import { FaPlus, FaFileExcel, FaSearch, FaFilePdf } from "react-icons/fa";
import UploadModal from "./components/UploadModal";
import CustomerTable from "./components/CustomerTable";
import VisitTable from "./components/VisitTable";
import AddCustomerForm from "./components/AddCustomerForm";
import { exportToExcel, exportToPDF } from "../../../utils/exportUtil";
import axios from "axios";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../../config";

const CustomerManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [customerData, setCustomerData] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adminPermissions, setAdminPermissions] = useState({
    customer: { add: false, edit: false, delete: false, upload: false }
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 50;

   const fetchAdminPermissions = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/admin/get-admin`, // Adjust this endpoint as per your API
          { headers: { token } }
        );
        
        if (response.data.success) {
          const adminData = response.data.result.admin;
          // console.log(adminData)
          setAdminPermissions(adminData.permissions);
          setIsSuperAdmin(adminData.isSuperAdmin || false);
        }
      } catch (error) {
        
        setAdminPermissions({
          customer: { add: false, edit: false, delete: false, upload: false }
        });
      }
    };
  useEffect(() => {
    fetchAdminPermissions();
    fetchCustomers(1);
  }, []);

  const fetchCustomers = async (pageNum = page) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/customer/get-all-customers?page=${pageNum}&limit=${LIMIT}`,
        { headers: { token } }
      );
      if (response.data.success) {
        setCustomerData(response.data.result);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setPage(pageNum);
      } else {
        toast.error("Failed to fetch customers");
      }
    } catch (error) {
      toast.error("Error fetching customers");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchCustomers(newPage);
  };

  const hasPermission = (action) => {
    return isSuperAdmin || (adminPermissions.customer && adminPermissions.customer[action]);
  };

  const handleRestrictedAction = (action, callback) => {
    if (!hasPermission(action)) {
      toast.error(`You don't have permission to ${action} customers`);
      return;
    }
    callback();
  };


  const fetchAllCustomersForExport = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `${API_BASE_URL}/api/customer/get-all-customers?page=1&limit=99999`,
      { headers: { token } }
    );
    if (res.data.success) return res.data.result;
    return [];
  };

  const buildAdminMap = async (allData) => {
    const token = localStorage.getItem("token");
    const adminMap = {};
    for (const cust of allData) {
      if (cust.addedBy && !adminMap[cust.addedBy]) {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/admin/get-subadmin/${cust.addedBy}`,
            { headers: { token } }
          );
          if (res.data.success && res.data.result?.subadmin) {
            adminMap[cust.addedBy] = res.data.result.subadmin.adminName;
          }
        } catch {}
      }
    }
    return adminMap;
  };

  const handleExportExcel = async () => {
    const loadingToast = toast.loading("Preparing Excel export...");
    try {
      const allData = await fetchAllCustomersForExport();
      const adminMap = await buildAdminMap(allData);
      const cleanedData = allData.map(
        ({ _id, status, createdAt, updatedAt, __v, addedBy, ...rest }) => ({
          ...rest,
          addedBy: adminMap[addedBy] || addedBy || "-",
        })
      );
      toast.dismiss(loadingToast);
      exportToExcel(cleanedData, "customers.xlsx");
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Export failed");
    }
  };

  const handleExportPDF = async () => {
    const loadingToast = toast.loading("Preparing PDF export...");
    try {
      const allData = await fetchAllCustomersForExport();
      const adminMap = await buildAdminMap(allData);
      const cleanedData = allData.map(
        ({
          customerName,
          customerNumber,
          leadDate,
          leadFrom,
          requiredFlatType,
          locationRequired,
          requiredResaleOrNewFlat,
          budget,
          followupDate,
          clientFeedback,
          customFeedbackCategory,
          freshCall,
          addedBy,
        }) => ({
          leadDate,
          leadFrom,
          customerName,
          customerNumber,
          requiredFlatType,
          locationRequired,
          requiredResaleOrNewFlat,
          budget,
          followupDate,
          clientFeedback,
          customFeedbackCategory,
          freshCall,
          addedBy: adminMap[addedBy] || addedBy || "-",
        })
      );
      toast.dismiss(loadingToast);
      exportToPDF(cleanedData, "customers.pdf", "customers");
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Export failed");
    }
  };


  // ✅ NEW: handleViewVisits fetches visit data from API
  const handleViewVisits = async (customer) => {
    try {
      const token = localStorage.getItem("token");
      const loadingToast = toast.loading("Fetching Visited Property...");
      const response = await axios.get(
        `${API_BASE_URL}/api/propertyVisit/get-visitsBycustomer/${customer._id}`,
        {
          headers: { token },
        }
      );
      toast.dismiss(loadingToast);
      if (response.data.success) {
        const visits = response.data.result.visits;
        setSelectedCustomer({ ...customer, visits });
        if (!visits || visits.length === 0) {
          toast.error("No visit data found for this customer.");
          // return;
        } else {
          toast.success("Visited Property fetched successfully!");
        }
      } else {
        toast.error("Failed to fetch visits");
      }
    } catch (error) {
      toast.error("Error fetching visits");
      // console.error(error);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">
            Customer Management
          </h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Total: {totalCount} customers &nbsp;|&nbsp; Page {page} of {totalPages}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded border px-3 py-2 pr-10 text-sm outline-none dark:bg-navy-700 dark:text-white"
            />
            <FaSearch className="absolute right-3 top-2.5 text-gray-500" />
          </div>

          <button
            onClick={() => handleRestrictedAction('upload', () => setShowUploadModal(true))}
            disabled={!hasPermission('upload')}
            className={`flex items-center gap-2 rounded px-4 py-2 text-white transition-all duration-200 ${
              hasPermission('upload')
                ? "bg-green-600 hover:bg-green-700 cursor-pointer"
                : "bg-gray-400 cursor-not-allowed opacity-60"
            }`}
            title={!hasPermission('upload') ? "You don't have permission to upload files" : "Upload Excel file"}
          >
            <FaFileExcel /> Upload Excel
          </button>

          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <FaFilePdf /> Export PDF
          </button>

          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 rounded bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            <FaFileExcel /> Export Excel
          </button>

          <button
            onClick={() => handleRestrictedAction('add', () => setShowAddForm(true))}
            disabled={!hasPermission('add')}
            className={`flex items-center gap-2 rounded px-4 py-2 text-white transition-all duration-200 ${
              hasPermission('add')
                ? "bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
                : "bg-gray-400 cursor-not-allowed opacity-60"
            }`}
            title={!hasPermission('add') ? "You don't have permission to add customers" : "Add new customers"}
          >
            <FaPlus /> Add Customer
          </button>
        </div>
      </div>

<CustomerTable
  search={searchTerm}
  data={customerData}
  setData={setCustomerData}
  onViewVisits={handleViewVisits}
  onRefresh={() => fetchCustomers(page)}
  adminPermissions={adminPermissions}
  isSuperAdmin={isSuperAdmin}
  page={page}
  totalPages={totalPages}
  onPageChange={handlePageChange}
/>

      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadSuccess={fetchCustomers}
        />
      )}

      {selectedCustomer?.visits && (
        <VisitTable
          // customer={selectedCustomer}
          // onClose={() => }
          customer={selectedCustomer}
  onClose={() => setSelectedCustomer(null)}
  adminPermissions={adminPermissions}
  isSuperAdmin={isSuperAdmin}
        />
      )}

      {showAddForm && (
        <AddCustomerForm
          onClose={() => setShowAddForm(false)}
          onAddSuccess={fetchCustomers}
          
        />
      )}
    </div>
  );
};

export default CustomerManagement;